import axios from "axios";
import { Product, CartItem } from "./models";

/**
 * Store client. Talks to the .NET API, which is the only thing holding the Stripe
 * secret key. Products and prices live in Stripe (the catalog); the API exposes
 * read-only product data and creates PaymentIntents with server-computed amounts.
 *
 * Configure the API base via REACT_APP_API_URL (see .env).
 */

const API_URL = (process.env.REACT_APP_API_URL ?? "http://localhost:5094").replace(/\/$/, "");

const api = axios.create({ baseURL: `${API_URL}/api/store` });

// Shape returned by GET /api/store/products (mirrors StoreProductDto).
interface StoreProductDto {
  id: string;
  priceId: string;
  name: string;
  description: string | null;
  images: string[];
  unitAmount: number; // pence
  currency: string;
  metadata: Record<string, string>;
}

// Map a Stripe-sourced product into the app's Product model.
// Variant data (sizes/colours/personalisation) can be driven from Stripe metadata.
const mapProduct = (dto: StoreProductDto): Product => {
  const meta = dto.metadata ?? {};
  const splitList = (v?: string) =>
    v ? v.split(",").map((s) => s.trim()).filter(Boolean) : [];

  return {
    id: dto.id,
    priceId: dto.priceId,
    name: dto.name,
    price: dto.unitAmount / 100,
    category: meta.category ?? "Other",
    artist: meta.artist ?? "",
    sizes: splitList(meta.sizes),
    colorOptions: splitList(meta.colours),
    canPersonalMessage: meta.canPersonalMessage === "true",
    outOfStock: meta.outOfStock === "true",
    isNew: meta.isNew === "true",
    imageUrls: dto.images?.length ? dto.images : ["/assets/img/placeholder.png"],
    description: dto.description ?? "",
    isActive: true,
    onSale: meta.onSale === "true",
    isDigital: meta.isDigital === "true",
    source: "stripe",
  };
};

// --- External products (Strapi store-items, e.g. music linking to Bandcamp) ---
const STRAPI_URL = (process.env.REACT_APP_STRAPI_URL ?? "http://localhost:1337").replace(/\/$/, "");
const STRAPI_TOKEN = process.env.REACT_APP_STRAPI_TOKEN ?? "";

const strapiApi = axios.create({
  baseURL: `${STRAPI_URL}/api`,
  headers: STRAPI_TOKEN ? { Authorization: `Bearer ${STRAPI_TOKEN}` } : undefined,
});

interface StrapiMedia {
  data: { attributes: { url: string } } | null;
}
interface StrapiStoreItemAttributes {
  name: string;
  artist: string | null;
  price: number | null;
  description: string | null;
  category: string | null;
  externalUrl: string;
  platform: string | null;
  soldOut: boolean | null;
  isNew: boolean | null;
  sortOrder: number | null;
  coverImage: StrapiMedia;
}
interface StrapiEntry<T> { id: number; attributes: T; }
interface StrapiListResponse<T> { data: StrapiEntry<T>[]; }

const absoluteStrapiUrl = (url?: string): string | null =>
  !url ? null : url.startsWith("http") ? url : `${STRAPI_URL}${url}`;

const mapStoreItem = (entry: StrapiEntry<StrapiStoreItemAttributes>): Product => {
  const a = entry.attributes;
  const img = absoluteStrapiUrl(a.coverImage?.data?.attributes?.url);
  return {
    id: `strapi-${entry.id}`,
    priceId: "",
    name: a.name,
    price: a.price ?? 0,
    category: a.category ?? "Music",
    artist: a.artist ?? "",
    sizes: [],
    canPersonalMessage: false,
    colorOptions: [],
    outOfStock: a.soldOut ?? false,
    isNew: a.isNew ?? false,
    imageUrls: img ? [img] : ["/assets/img/placeholder.png"],
    description: a.description ?? "",
    isActive: true,
    onSale: false,
    isDigital: true,
    source: "external",
    externalUrl: a.externalUrl,
    platform: a.platform ?? "Bandcamp",
  };
};

const getExternalProducts = async (): Promise<Product[]> => {
  try {
    const res = await strapiApi.get<StrapiListResponse<StrapiStoreItemAttributes>>("/store-items", {
      params: {
        populate: "*",
        "sort[0]": "sortOrder:asc",
        "sort[1]": "name:asc",
        "pagination[pageSize]": 100,
      },
    });
    return res.data.data.map(mapStoreItem);
  } catch {
    // Strapi being unavailable shouldn't break the Stripe-backed store.
    return [];
  }
};

// Pool Stripe products + Strapi external items into one list for the store.
export const getProducts = async (): Promise<Product[]> => {
  const [stripeRes, external] = await Promise.all([
    api.get<StoreProductDto[]>("/products"),
    getExternalProducts(),
  ]);
  return [...stripeRes.data.map(mapProduct), ...external];
};

export interface StripeConfig {
  publishableKey: string;
  currency: string;
}

export const getStripeConfig = async (): Promise<StripeConfig> => {
  const response = await api.get<StripeConfig>("/config");
  return response.data;
};

export interface PaymentIntentResponse {
  clientSecret: string;
  amount: number; // pence
  currency: string;
}

// Sends only price ids + quantities; the server computes the real total from Stripe.
export const createPaymentIntent = async (
  cartItems: CartItem[],
  email?: string
): Promise<PaymentIntentResponse> => {
  const items = cartItems.map((item) => ({
    priceId: item.product.priceId,
    quantity: item.quantity,
  }));
  const response = await api.post<PaymentIntentResponse>("/payment-intent", {
    items,
    email,
  });
  return response.data;
};
